import React, { useState, useRef, useEffect, useMemo } from 'react';
import api from './services/api';
import Header from './components/Header';
import PasswordInput from './components/PasswordInput';
import SmsConsentCheckbox from './components/SmsConsentCheckbox';
import FriendSuggestionModal from './components/FriendSuggestionModal';
import { useTheme, themes } from './contexts/ThemeContext';
import { getAuthenticatedMediaUrl } from './utils/mediaUtils';
import { formatDateOnly } from './utils/dateUtils';
import CurrentPlanStatus from './components/Subscription/CurrentPlanStatus';
import PlanComparisonCard from './components/Subscription/PlanComparisonCard';
import FamilySettings from './components/Family/FamilySettings';
import './SettingsPage.css';

function SettingsPage({ user, token, onNavigateHome, onNavigateAdmin, onLogout, isAdmin, onUserUpdate, initialTab, onTabChange, onPreferenceChange, onNavigateToPrompts, onNavigateToHome, stickyHeader }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'profile');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const fileInputRef = useRef(null);
  const { currentTheme, applyTheme } = useTheme();

  // Profile form data
  const [profileData, setProfileData] = useState({
    email: user?.email || '',
    phone: '',
    timezone: 'America/New_York',
    headshotUrl: user?.headshotUrl || null,
    authMethod: user?.authMethod || 'email'
  });

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    weeklyDigest: true,
    reflectionReminders: true
  });

  // Email and SMS reminder settings
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [smsRemindersEnabled, setSmsRemindersEnabled] = useState(false);
  const [smsReminderFrequency, setSmsReminderFrequency] = useState('weekly');

  // Sharing settings
  const [sharingSettings, setSharingSettings] = useState({
    defaultPrivacy: 'private',
    allowPublicProfile: false,
    shareWithFriends: true
  });

  // Share management
  const [shareEmail, setShareEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [shareRelationships, setShareRelationships] = useState({
    sharingWith: [],
    sharedWithMe: [],
    pendingInvitations: []
  });

  // Phone verification modal
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingPhone, setPendingPhone] = useState('');

  // SMS consent tracking
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsConsentTimestamp, setSmsConsentTimestamp] = useState(null);
  const [removeShareModal, setRemoveShareModal] = useState({ show: false, relationship: null });

  // Friend suggestions
  const [friends, setFriends] = useState([]);
  const [mySuggestions, setMySuggestions] = useState([]);
  const [friendSuggestionModal, setFriendSuggestionModal] = useState({ show: false, friend: null });

  // Subscription
  const [subscription, setSubscription] = useState(null);
  const [subscriptionStorageStatus, setSubscriptionStorageStatus] = useState(null);

  // Delete account modal
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState('');
  const [showDeletionComplete, setShowDeletionComplete] = useState(false);

  // User preferences
  const [preferences, setPreferences] = useState({
    default_reflection_type: 'video'
  });
  const [preferencesSaving, setPreferencesSaving] = useState(false);

  // Welcome screen preference
  const [hideWelcomeScreen, setHideWelcomeScreen] = useState(false);
  const [hideWelcomeSaving, setHideWelcomeSaving] = useState(false);

  // Helper to change tab and update URL
  const changeTab = (tab) => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        email: user.email || '',
        phone: user.phone || '',
        timezone: user.timezone || prev.timezone,
        headshotUrl: user.headshotUrl || null,
        authMethod: user.authMethod || 'email'
      }));
      // Load SMS consent data if available
      setSmsConsent(user.smsConsentGiven || false);
      setSmsConsentTimestamp(user.smsConsentTimestamp || null);
      // Load email notifications preference
      if (user.emailNotificationsEnabled !== undefined) {
        setEmailNotificationsEnabled(user.emailNotificationsEnabled);
      }
      // Load hide welcome screen preference
      if (user.hideWelcomeScreen !== undefined) {
        setHideWelcomeScreen(user.hideWelcomeScreen);
      }
    }
  }, [user]);

  useEffect(() => {
    // Fetch preferences on component mount
    fetchPreferences();
  }, []);

  // Sync SMS preferences from database to state
  useEffect(() => {
    if (preferences.sms_reminders_enabled !== undefined) {
      setSmsRemindersEnabled(preferences.sms_reminders_enabled);
    }
    if (preferences.sms_reminder_frequency) {
      setSmsReminderFrequency(preferences.sms_reminder_frequency);
    }
  }, [preferences]);

  // Get authenticated headshot URL for display (but not for data URLs from file reader)
  const displayHeadshotUrl = useMemo(() => {
    if (!profileData.headshotUrl) return null;
    // If it's a data URL (from file preview), use it directly
    if (profileData.headshotUrl.startsWith('data:')) return profileData.headshotUrl;
    // Otherwise, add authentication token
    return getAuthenticatedMediaUrl(profileData.headshotUrl);
  }, [profileData.headshotUrl]);

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleNotificationChange = (field, value) => {
    setNotificationSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSharingChange = (field, value) => {
    setSharingSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileData(prev => ({ ...prev, headshotUrl: reader.result }));
      setHasChanges(true);
    };
    reader.readAsDataURL(file);

    // Upload file
    await uploadHeadshot(file);
  };

  const uploadHeadshot = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('headshot', file);

      const response = await api.post('/upload/headshot', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update profile data with new URL from server
      setProfileData(prev => ({ ...prev, headshotUrl: response.data.headshotUrl }));

      // Update parent component's user state
      if (onUserUpdate) {
        onUserUpdate({ ...user, headshotUrl: response.data.headshotUrl });
      }

      alert('Profile picture updated successfully!');
    } catch (error) {
      console.error('Error uploading headshot:', error);
      alert('Failed to upload profile picture: ' + (error.response?.data?.error || error.message));
      setProfileData(prev => ({ ...prev, headshotUrl: user?.headshotUrl || null }));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveHeadshot = async () => {
    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }

    setUploading(true);
    try {
      await api.delete('/upload/headshot');

      setProfileData(prev => ({ ...prev, headshotUrl: null }));

      // Update parent component's user state
      if (onUserUpdate) {
        onUserUpdate({ ...user, headshotUrl: null });
      }

      setHasChanges(true);
      alert('Profile picture removed successfully!');
    } catch (error) {
      console.error('Error removing headshot:', error);
      alert('Failed to remove profile picture: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      // Save profile data (excluding headshot which is handled separately)
      const response = await api.put('/users/profile', {
        email: profileData.email,
        phone: profileData.phone,
        timezone: profileData.timezone
      });

      // Check if verification is needed
      if (response.data.verificationType === 'sms' || response.data.verificationType === 'both') {
        setPendingPhone(profileData.phone);
        setShowPhoneVerification(true);
        setHasChanges(false);
        alert(response.data.details || 'Please check your phone for a verification code.');
      } else {
        // No verification needed, update succeeded
        setHasChanges(false);
        if (response.data.user) {
          onUserUpdate(response.data.user);
        }
        alert('Settings saved successfully!');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyPhoneCode = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users/verify-phone-change', {
        phone: pendingPhone,
        code: verificationCode
      });

      // Success - close modal and refresh user data
      setShowPhoneVerification(false);
      setVerificationCode('');
      setPendingPhone('');

      // Fetch updated user data
      const userResponse = await api.get('/auth/me');
      onUserUpdate(userResponse.data);

      alert('Phone number verified and updated successfully!');
    } catch (error) {
      console.error('Error verifying phone code:', error);
      alert(error.response?.data?.error || 'Failed to verify code. Please try again.');
    }
  };

  // Preferences functions
  const fetchPreferences = async () => {
    try {
      const response = await api.get('/preferences');
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      // Don't alert - just use defaults
    }
  };

  const handlePreferenceChange = async (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setPreferencesSaving(true);
    try {
      await api.put('/preferences', {
        preferenceKey: key,
        preferenceValue: value
      });
      // Notify parent component (App.js) so effectiveAISettings updates immediately
      if (onPreferenceChange) {
        onPreferenceChange(key, value);
      }
    } catch (error) {
      console.error('Error saving preference:', error);
      alert('Failed to save preference: ' + (error.response?.data?.error || error.message));
    } finally {
      setPreferencesSaving(false);
    }
  };

  const handleHideWelcomeScreenChange = async (value) => {
    setHideWelcomeScreen(value);
    setHideWelcomeSaving(true);
    try {
      await api.put('/auth/preferences/hide-welcome-screen', {
        hideWelcomeScreen: value
      });
    } catch (error) {
      console.error('Error saving hide welcome screen preference:', error);
      alert('Failed to save preference: ' + (error.response?.data?.error || error.message));
      // Revert on error
      setHideWelcomeScreen(!value);
    } finally {
      setHideWelcomeSaving(false);
    }
  };

  // Share management functions
  const fetchShareRelationships = async () => {
    try {
      const response = await api.get('/sharing/relationships');
      setShareRelationships(response.data);
    } catch (error) {
      console.error('Error fetching share relationships:', error);
      alert('Failed to fetch share relationships: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchFriends = async () => {
    try {
      const response = await api.get('/friend-suggestions/friends');
      setFriends(response.data);
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  };

  const fetchMySuggestions = async () => {
    try {
      const response = await api.get('/friend-suggestions/my-suggestions');
      setMySuggestions(response.data);
    } catch (error) {
      console.error('Error fetching my suggestions:', error);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!shareEmail) return;

    setSendingInvite(true);
    try {
      const response = await api.post('/sharing/invite',
        { email: shareEmail }
      );

      alert(response.data.message);
      setShareEmail('');
      fetchShareRelationships();
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Failed to send invitation: ' + (error.response?.data?.error || error.message));
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRevokeShare = async (relationshipId, userIdentifier) => {
    if (!window.confirm(`Are you sure you want to stop sharing with ${userIdentifier}?`)) {
      return;
    }

    try {
      await api.delete(`/sharing/relationship/${relationshipId}`);
      alert('Share access revoked successfully');
      fetchShareRelationships();
    } catch (error) {
      console.error('Error revoking share:', error);
      alert('Failed to revoke access: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleResendInvitation = async (email) => {
    try {
      const response = await api.post('/sharing/invite',
        { email: email }
      );
      alert(response.data.message);
      fetchShareRelationships();
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Failed to resend invitation: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancelInvitation = async (invitationId, email) => {
    if (!window.confirm(`Are you sure you want to cancel the invitation to ${email}?`)) {
      return;
    }

    try {
      await api.delete(`/sharing/invitation/${invitationId}`);
      alert('Invitation cancelled successfully');
      fetchShareRelationships();
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      alert('Failed to cancel invitation: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAcceptShare = async (relationshipId, sharerEmail) => {
    try {
      await api.post(`/sharing/accept/${relationshipId}`, {});
      alert(`You are now sharing reflections with ${sharerEmail}`);
      fetchShareRelationships();
    } catch (error) {
      console.error('Error accepting share:', error);
      alert('Failed to accept invitation: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeclineShare = async (relationshipId, sharerEmail) => {
    if (!window.confirm(`Are you sure you want to decline the invitation from ${sharerEmail}?`)) {
      return;
    }

    try {
      await api.post(`/sharing/decline/${relationshipId}`, {});
      alert('Invitation declined');
      fetchShareRelationships();
    } catch (error) {
      console.error('Error declining share:', error);
      alert('Failed to decline invitation: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRemoveShare = async () => {
    const { relationship } = removeShareModal;
    if (!relationship) return;

    try {
      await api.post(`/sharing/decline/${relationship.id}`, {});
      setRemoveShareModal({ show: false, relationship: null });
      alert(`You have removed the share from ${relationship.username}`);
      fetchShareRelationships();
    } catch (error) {
      console.error('Error removing share:', error);
      alert('Failed to remove share: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeletePasswordError('');

    try {
      await api.delete('/users/account', {
        data: { password: deletePassword }
      });

      // Account deleted successfully
      setShowDeleteAccountModal(false);
      setShowDeletionComplete(true);
    } catch (error) {
      if (error.response?.status === 401) {
        setDeletePasswordError('Invalid password');
      } else {
        alert('Failed to delete account: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const handleDeletionComplete = () => {
    setShowDeletionComplete(false);
    setDeletePassword('');
    onLogout();
  };

  // Load share relationships when sharing tab is active
  useEffect(() => {
    if (activeTab === 'sharing') {
      fetchShareRelationships();
      fetchFriends();
      fetchMySuggestions();
    }
  }, [activeTab]);

  // Load subscription + storage status when subscription tab is active
  useEffect(() => {
    if (activeTab === 'subscription' && !subscription) {
      api.get('/subscriptions/current')
        .then(r => setSubscription(r.data))
        .catch(err => console.error('Failed to load subscription:', err));
      api.get('/users/storage-status')
        .then(r => setSubscriptionStorageStatus(r.data))
        .catch(err => console.error('Failed to load storage status:', err));
    }
  }, [activeTab, subscription]);

  return (
    <div className="settings-page">
      <Header
        user={user}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onSettingsClick={() => {}} // Already on settings page
        onAdminClick={onNavigateAdmin}
        onLogout={onLogout}
        isAdmin={isAdmin}
        onTitleClick={onNavigateHome}
        onNavigateToPrompts={onNavigateToPrompts}
        onNavigateToHome={onNavigateToHome}
        stickyHeader={stickyHeader}
      />

      <div className="settings-container">
        <div className="settings-header">
          <h2>Settings</h2>
          {hasChanges && (
            <button
              className="save-changes-btn"
              onClick={handleSaveChanges}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>

        <div className="settings-tabs">
          <button
            className={activeTab === 'profile' ? 'active' : ''}
            onClick={() => changeTab('profile')}
          >
            Profile
          </button>
          <button
            className={activeTab === 'preferences' ? 'active' : ''}
            onClick={() => changeTab('preferences')}
          >
            Preferences
          </button>
          <button
            className={activeTab === 'notifications' ? 'active' : ''}
            onClick={() => changeTab('notifications')}
          >
            Notifications
          </button>
          <button
            className={activeTab === 'sharing' ? 'active' : ''}
            onClick={() => changeTab('sharing')}
          >
            Sharing
          </button>
          <button
            className={activeTab === 'subscription' ? 'active' : ''}
            onClick={() => changeTab('subscription')}
          >
            Plan &amp; Billing
          </button>
          {subscription?.allowsFamily && (
            <button
              className={activeTab === 'family' ? 'active' : ''}
              onClick={() => changeTab('family')}
            >
              Family
            </button>
          )}
        </div>

        <div className="settings-content">
          {activeTab === 'profile' && (
            <div className="settings-section">
              <h3>Profile Settings</h3>

              <div className="profile-headshot-section">
                <label>Profile Picture</label>
                <div className="headshot-container">
                  <div className="headshot-preview">
                    {displayHeadshotUrl ? (
                      <img
                        src={displayHeadshotUrl}
                        alt="Profile preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <span className="headshot-placeholder">
                        {user?.username?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="headshot-actions">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      disabled={uploading}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="btn-upload"
                    >
                      {uploading ? 'Uploading...' : (profileData.headshotUrl ? 'Change Picture' : 'Upload Picture')}
                    </button>
                    {profileData.headshotUrl && (
                      <button
                        onClick={handleRemoveHeadshot}
                        disabled={uploading}
                        className="btn-remove"
                      >
                        Remove
                      </button>
                    )}
                    <p className="upload-hint">
                      Supported formats: JPEG, PNG, GIF, WebP (max 5MB)
                    </p>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={user?.username || ''}
                  readOnly
                  className="readonly-field"
                  title="Username cannot be changed"
                />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  placeholder="(###) ###-####"
                  value={profileData.phone}
                  onChange={(e) => handleProfileChange('phone', e.target.value)}
                />
              </div>

              {/* SMS Consent Checkbox - Show if user has a phone number or is adding one */}
              {profileData.phone && (
                <SmsConsentCheckbox
                  checked={smsConsent}
                  onChange={async (newConsent) => {
                    setSmsConsent(newConsent);
                    // Save consent change immediately
                    try {
                      await api.post('/users/sms-consent', {
                        consentGiven: newConsent,
                        consentVersion: '1.0'
                      });
                      if (newConsent) {
                        setSmsConsentTimestamp(new Date().toISOString());
                      } else {
                        setSmsConsentTimestamp(null);
                      }
                      // Refresh user data
                      const userResponse = await api.get('/auth/me');
                      onUserUpdate(userResponse.data);
                    } catch (error) {
                      console.error('Error updating SMS consent:', error);
                      alert('Failed to update SMS consent: ' + (error.response?.data?.error || error.message));
                      // Revert on error
                      setSmsConsent(!newConsent);
                    }
                  }}
                  consentTimestamp={smsConsentTimestamp}
                  context="profile"
                  timezone={user?.timezone}
                />
              )}

              <div className="form-group">
                <label>Verification Method</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <select
                    value={profileData.authMethod || 'email'}
                    onChange={(e) => handleProfileChange('authMethod', e.target.value)}
                  >
                    <option value="email" disabled={!profileData.email}>Email Verification</option>
                    <option value="sms" disabled={!profileData.phone}>SMS One-Time Code</option>
                  </select>
                  {profileData.authMethod !== (user?.authMethod || 'email') && (
                    <button
                      type="button"
                      className="btn-save"
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.85rem' }}
                      onClick={async () => {
                        try {
                          const res = await api.put('/auth/auth-method', {
                            authMethod: profileData.authMethod
                          });
                          alert(res.data.message);
                          const userResponse = await api.get('/auth/me');
                          onUserUpdate(userResponse.data);
                        } catch (error) {
                          alert(error.response?.data?.error || 'Failed to change verification method');
                        }
                      }}
                    >
                      Save
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Timezone</label>
                <select
                  value={profileData.timezone}
                  onChange={(e) => handleProfileChange('timezone', e.target.value)}
                >
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="America/Anchorage">Alaska Time (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                </select>
              </div>

              <div className="danger-zone" style={{
                marginTop: '3rem',
                paddingTop: '2rem',
                borderTop: '1px solid var(--color-border)'
              }}>
                <h4 style={{ color: 'var(--color-danger)', marginBottom: '1rem' }}>Danger Zone</h4>
                <p style={{ marginBottom: '1rem', color: 'var(--color-text-light)' }}>
                  Once you delete your account, there is no going back. This action cannot be undone.
                </p>
                <button
                  onClick={() => setShowDeleteAccountModal(true)}
                  className="btn-danger"
                  style={{
                    background: 'var(--color-danger)',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: 'var(--radius-base, 0.5rem)',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Delete Account
                </button>
              </div>

            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="settings-section">
              <h3>Preferences</h3>
              <p className="section-description">Customize your default reflection settings</p>

              {/* Theme */}
              <div className="settings-row">
                <div className="settings-row-info">
                  <div className="settings-row-label">Theme</div>
                  <p className="settings-row-description">Choose your preferred color scheme and visual style</p>
                </div>
                <div className="settings-row-control">
                  <select
                    value={currentTheme}
                    onChange={async (e) => {
                      const newTheme = e.target.value;
                      applyTheme(newTheme);
                      try {
                        await api.put('/preferences', {
                          preferenceKey: 'theme',
                          preferenceValue: newTheme
                        });
                      } catch (error) {
                        console.error('Error saving theme preference:', error);
                      }
                    }}
                  >
                    {Object.entries(themes).map(([key, theme]) => (
                      <option key={key} value={key}>
                        {theme.icon} {theme.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preferred Reflection Type */}
              <div className="settings-row">
                <div className="settings-row-info">
                  <div className="settings-row-label">Preferred Reflection Type</div>
                  <p className="settings-row-description">
                    Default type when you create a new reflection
                    {preferencesSaving && ' (Saving...)'}
                  </p>
                </div>
                <div className="settings-row-control">
                  <select
                    id="defaultReflectionType"
                    value={preferences.default_reflection_type || 'video'}
                    onChange={(e) => handlePreferenceChange('default_reflection_type', e.target.value)}
                    disabled={preferencesSaving}
                  >
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                    <option value="text">Text</option>
                  </select>
                </div>
              </div>

              {/* Hide Welcome Screen */}
              <div className="settings-row">
                <div className="settings-row-info">
                  <div className="settings-row-label">Hide welcome screen</div>
                  <p className="settings-row-description">
                    Skip the welcome and welcome back dialogs on login
                    {hideWelcomeSaving && ' (Saving...)'}
                  </p>
                </div>
                <div className="settings-row-control">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={hideWelcomeScreen}
                      onChange={(e) => handleHideWelcomeScreenChange(e.target.checked)}
                      disabled={hideWelcomeSaving}
                    />
                    <span className={`toggle-switch-track${hideWelcomeScreen ? ' active' : ''}${hideWelcomeSaving ? ' disabled' : ''}`}>
                      <span className={`toggle-switch-knob${hideWelcomeScreen ? ' active' : ''}`}></span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Show Reflection Sizes */}
              <div className="settings-row">
                <div className="settings-row-info">
                  <div className="settings-row-label">Show reflection sizes</div>
                  <p className="settings-row-description">
                    Display the total media file size on each reflection card
                    {preferencesSaving && ' (Saving...)'}
                  </p>
                </div>
                <div className="settings-row-control">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.show_reflection_sizes === true || preferences.show_reflection_sizes === 'true'}
                      onChange={(e) => handlePreferenceChange('show_reflection_sizes', e.target.checked)}
                      disabled={preferencesSaving}
                    />
                    <span className={`toggle-switch-track${(preferences.show_reflection_sizes === true || preferences.show_reflection_sizes === 'true') ? ' active' : ''}${preferencesSaving ? ' disabled' : ''}`}>
                      <span className={`toggle-switch-knob${(preferences.show_reflection_sizes === true || preferences.show_reflection_sizes === 'true') ? ' active' : ''}`}></span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Sticky Header */}
              <div className="settings-row">
                <div className="settings-row-info">
                  <div className="settings-row-label">Sticky header</div>
                  <p className="settings-row-description">
                    Keep the header fixed at the top of the page while scrolling
                    {preferencesSaving && ' (Saving...)'}
                  </p>
                </div>
                <div className="settings-row-control">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.sticky_header === true || preferences.sticky_header === 'true'}
                      onChange={(e) => handlePreferenceChange('sticky_header', e.target.checked)}
                      disabled={preferencesSaving}
                    />
                    <span className={`toggle-switch-track${(preferences.sticky_header === true || preferences.sticky_header === 'true') ? ' active' : ''}${preferencesSaving ? ' disabled' : ''}`}>
                      <span className={`toggle-switch-knob${(preferences.sticky_header === true || preferences.sticky_header === 'true') ? ' active' : ''}`}></span>
                    </span>
                  </label>
                </div>
              </div>

              {/* AI Usage Section */}
              <div className="settings-section-divider">
                <h4>AI Usage</h4>
                <p>
                  Control how AI features are used with your reflections. When AI is authorized, your content may be sent to OpenAI for processing.
                </p>
                <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary)', fontSize: '0.85rem', lineHeight: '1.5' }}>
                  OpenAI does not use API data for training models.{' '}
                  <a
                    href="https://openai.com/policies/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    Learn more about OpenAI's privacy policy
                  </a>
                </p>

                {/* Authorize AI Toggle */}
                <div className="settings-row">
                  <div className="settings-row-info">
                    <div className="settings-row-label">Authorize AI</div>
                    <p className="settings-row-description">
                      {(preferences.authorize_ai === true || preferences.authorize_ai === 'true')
                        ? 'AI features are enabled. Your content may be processed by OpenAI.'
                        : 'AI features are disabled. No data will be sent to AI services.'}
                    </p>
                  </div>
                  <div className="settings-row-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        id="authorizeAi"
                        checked={preferences.authorize_ai === true || preferences.authorize_ai === 'true'}
                        onChange={(e) => handlePreferenceChange('authorize_ai', e.target.checked)}
                        disabled={preferencesSaving}
                      />
                      <span className={`toggle-switch-track${(preferences.authorize_ai === true || preferences.authorize_ai === 'true') ? ' active' : ''}${preferencesSaving ? ' disabled' : ''}`}>
                        <span className={`toggle-switch-knob${(preferences.authorize_ai === true || preferences.authorize_ai === 'true') ? ' active' : ''}`}></span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Transcription Setting */}
                <div className="settings-row">
                  <div className="settings-row-info">
                    <div className="settings-row-label">Transcription Setting</div>
                    <p className="settings-row-description">
                      {!(preferences.authorize_ai === true || preferences.authorize_ai === 'true')
                        ? 'Enable "Authorize AI" above to configure transcription settings.'
                        : preferences.transcription_preference === 'never'
                          ? 'Your audio and video reflections will not be transcribed.'
                          : (preferences.transcription_preference === 'on_demand' || !preferences.transcription_preference)
                            ? 'A "Transcribe" button will appear on each reflection. You decide which ones to transcribe.'
                            : preferences.transcription_preference === 'always'
                              ? 'New recordings will be automatically transcribed after you save them.'
                              : ''}
                    </p>
                  </div>
                  <div className="settings-row-control">
                    <select
                      id="transcriptionPreference"
                      value={(preferences.authorize_ai === true || preferences.authorize_ai === 'true') ? (preferences.transcription_preference || 'on_demand') : 'never'}
                      onChange={(e) => handlePreferenceChange('transcription_preference', e.target.value)}
                      disabled={preferencesSaving || !(preferences.authorize_ai === true || preferences.authorize_ai === 'true')}
                    >
                      <option value="never">Never Transcribe</option>
                      <option value="on_demand">Only when I choose</option>
                      <option value="always">Always transcribe</option>
                    </select>
                  </div>
                </div>

                {/* Automate Titles */}
                <div className={`settings-row${!(preferences.authorize_ai === true || preferences.authorize_ai === 'true') ? ' disabled' : ''}`}>
                  <div className="settings-row-info">
                    <div className="settings-row-label">Automate Titles</div>
                    <p className="settings-row-description">
                      {!(preferences.authorize_ai === true || preferences.authorize_ai === 'true')
                        ? 'Enable "Authorize AI" to use this feature.'
                        : 'Automatically generate titles for your reflections using AI.'}
                    </p>
                  </div>
                  <div className="settings-row-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={(preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.automate_titles === true || preferences.automate_titles === 'true')}
                        onChange={(e) => handlePreferenceChange('automate_titles', e.target.checked)}
                        disabled={preferencesSaving || !(preferences.authorize_ai === true || preferences.authorize_ai === 'true')}
                      />
                      <span className={`toggle-switch-track${((preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.automate_titles === true || preferences.automate_titles === 'true')) ? ' active' : ''}${(preferencesSaving || !(preferences.authorize_ai === true || preferences.authorize_ai === 'true')) ? ' disabled' : ''}`}>
                        <span className={`toggle-switch-knob${((preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.automate_titles === true || preferences.automate_titles === 'true')) ? ' active' : ''}`}></span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Enable Follow-up Questions */}
                <div className={`settings-row${!(preferences.authorize_ai === true || preferences.authorize_ai === 'true') ? ' disabled' : ''}`}>
                  <div className="settings-row-info">
                    <div className="settings-row-label">Enable Follow-up Questions</div>
                    <p className="settings-row-description">
                      {!(preferences.authorize_ai === true || preferences.authorize_ai === 'true')
                        ? 'Enable "Authorize AI" to use this feature.'
                        : (preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined)
                          ? 'AI will suggest follow-up questions to deepen your reflections.'
                          : 'AI follow-up suggestions and the Enhance button are hidden.'}
                    </p>
                  </div>
                  <div className="settings-row-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={(preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined)}
                        onChange={(e) => handlePreferenceChange('enable_followups', e.target.checked)}
                        disabled={preferencesSaving || !(preferences.authorize_ai === true || preferences.authorize_ai === 'true')}
                      />
                      <span className={`toggle-switch-track${((preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined)) ? ' active' : ''}${(preferencesSaving || !(preferences.authorize_ai === true || preferences.authorize_ai === 'true')) ? ' disabled' : ''}`}>
                        <span className={`toggle-switch-knob${((preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined)) ? ' active' : ''}`}></span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Automatically Generate Follow-up Questions */}
                <div className={`settings-row${!((preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined)) ? ' disabled' : ''}`}>
                  <div className="settings-row-info">
                    <div className="settings-row-label">Automatically Generate Follow-up Questions</div>
                    <p className="settings-row-description">
                      {!(preferences.authorize_ai === true || preferences.authorize_ai === 'true')
                        ? 'Enable "Authorize AI" to use this feature.'
                        : !(preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined)
                          ? 'Enable "Follow-up Questions" to use this feature.'
                          : (preferences.auto_enhance === true || preferences.auto_enhance === 'true' || preferences.auto_enhance === undefined)
                            ? 'Follow-up questions will be generated automatically after transcription.'
                            : 'You can still generate follow-up questions manually with the Enhance button.'}
                    </p>
                  </div>
                  <div className="settings-row-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={(preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined) && (preferences.auto_enhance === true || preferences.auto_enhance === 'true' || preferences.auto_enhance === undefined)}
                        onChange={(e) => handlePreferenceChange('auto_enhance', e.target.checked)}
                        disabled={preferencesSaving || !(preferences.authorize_ai === true || preferences.authorize_ai === 'true') || !(preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined)}
                      />
                      <span className={`toggle-switch-track${((preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined) && (preferences.auto_enhance === true || preferences.auto_enhance === 'true' || preferences.auto_enhance === undefined)) ? ' active' : ''}${(preferencesSaving || !(preferences.authorize_ai === true || preferences.authorize_ai === 'true') || !(preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined)) ? ' disabled' : ''}`}>
                        <span className={`toggle-switch-knob${((preferences.authorize_ai === true || preferences.authorize_ai === 'true') && (preferences.enable_followups === true || preferences.enable_followups === 'true' || preferences.enable_followups === undefined) && (preferences.auto_enhance === true || preferences.auto_enhance === 'true' || preferences.auto_enhance === undefined)) ? ' active' : ''}`}></span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Follow-up Display Mode */}
                <div className={`settings-row${!(preferences.authorize_ai === true || preferences.authorize_ai === 'true') ? ' disabled' : ''}`}>
                  <div className="settings-row-info">
                    <div className="settings-row-label">How to Display Follow-up Reflections</div>
                    <p className="settings-row-description">
                      {!(preferences.authorize_ai === true || preferences.authorize_ai === 'true')
                        ? 'Enable "Authorize AI" to configure follow-up display.'
                        : (preferences.followup_display_mode || 'only_as_threads') === 'only_as_threads'
                          ? 'Follow-up reflections are only visible through the followups view.'
                          : 'Follow-up reflections appear alongside all other reflections in the prompt list.'}
                    </p>
                  </div>
                  <div className="settings-row-control">
                    <select
                      value={(preferences.authorize_ai === true || preferences.authorize_ai === 'true') ? (preferences.followup_display_mode || 'only_as_threads') : 'only_as_threads'}
                      onChange={(e) => handlePreferenceChange('followup_display_mode', e.target.value)}
                      disabled={preferencesSaving || !(preferences.authorize_ai === true || preferences.authorize_ai === 'true')}
                    >
                      <option value="only_as_threads">Only as Followups</option>
                      <option value="with_all_others">With all others</option>
                    </select>
                  </div>
                </div>

                {preferencesSaving && (
                  <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--color-text-secondary)' }}>
                    Saving...
                  </small>
                )}
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h3>Notification Settings</h3>
              <p className="section-description">Manage how and when you receive notifications</p>

              {/* Email Notifications */}
              <div className="settings-row">
                <div className="settings-row-info">
                  <div className="settings-row-label">Email Notifications</div>
                  <p className="settings-row-description">Receive email updates about your reflections</p>
                </div>
                <div className="settings-row-control">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.emailNotifications}
                      onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                    />
                    <span className={`toggle-switch-track${notificationSettings.emailNotifications ? ' active' : ''}`}>
                      <span className={`toggle-switch-knob${notificationSettings.emailNotifications ? ' active' : ''}`}></span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Push Notifications */}
              <div className="settings-row">
                <div className="settings-row-info">
                  <div className="settings-row-label">Push Notifications</div>
                  <p className="settings-row-description">Get real-time browser notifications</p>
                </div>
                <div className="settings-row-control">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.pushNotifications}
                      onChange={(e) => handleNotificationChange('pushNotifications', e.target.checked)}
                    />
                    <span className={`toggle-switch-track${notificationSettings.pushNotifications ? ' active' : ''}`}>
                      <span className={`toggle-switch-knob${notificationSettings.pushNotifications ? ' active' : ''}`}></span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Weekly Digest */}
              <div className="settings-row">
                <div className="settings-row-info">
                  <div className="settings-row-label">Weekly Digest</div>
                  <p className="settings-row-description">Receive a weekly summary of your reflections</p>
                </div>
                <div className="settings-row-control">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.weeklyDigest}
                      onChange={(e) => handleNotificationChange('weeklyDigest', e.target.checked)}
                    />
                    <span className={`toggle-switch-track${notificationSettings.weeklyDigest ? ' active' : ''}`}>
                      <span className={`toggle-switch-knob${notificationSettings.weeklyDigest ? ' active' : ''}`}></span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Reflection Reminders */}
              <div className="settings-row">
                <div className="settings-row-info">
                  <div className="settings-row-label">Reflection Reminders</div>
                  <p className="settings-row-description">Get reminders to record your daily reflections</p>
                </div>
                <div className="settings-row-control">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.reflectionReminders}
                      onChange={(e) => handleNotificationChange('reflectionReminders', e.target.checked)}
                    />
                    <span className={`toggle-switch-track${notificationSettings.reflectionReminders ? ' active' : ''}`}>
                      <span className={`toggle-switch-knob${notificationSettings.reflectionReminders ? ' active' : ''}`}></span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Email Reflection Reminders Section */}
              <div className="settings-section-divider">
                <h4>Email Reflection Reminders</h4>

                <div className="settings-row">
                  <div className="settings-row-info">
                    <div className="settings-row-label">Enable Email Reflection Reminders</div>
                    <p className="settings-row-description">Receive email reminders to create reflections based on your frequency preference</p>
                  </div>
                  <div className="settings-row-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={emailNotificationsEnabled}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          setEmailNotificationsEnabled(newValue);
                          try {
                            await api.put('/users/me', {
                              emailNotificationsEnabled: newValue
                            });
                          } catch (error) {
                            console.error('Error saving email notification preference:', error);
                            setEmailNotificationsEnabled(!newValue);
                          }
                        }}
                      />
                      <span className={`toggle-switch-track${emailNotificationsEnabled ? ' active' : ''}`}>
                        <span className={`toggle-switch-knob${emailNotificationsEnabled ? ' active' : ''}`}></span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* SMS Reflection Reminders Section */}
              <div className="settings-section-divider">
                <h4>SMS Reflection Reminders</h4>

                {!(smsConsent && user?.phone) ? (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'var(--color-bg-light)',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)'
                  }}>
                    <p style={{ marginBottom: '0.5rem', color: 'var(--color-text)' }}>
                      To enable SMS reflection reminders, you must first provide SMS consent in the{' '}
                      <button
                        onClick={() => changeTab('profile')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--color-primary)',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit'
                        }}
                      >
                        Profile tab
                      </button>.
                    </p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-light)', marginTop: '0.5rem', marginBottom: 0 }}>
                      You'll need to have a phone number on file and agree to our SMS terms.
                    </p>
                  </div>
                ) : !user?.phoneVerified ? (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'var(--color-bg-light)',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)'
                  }}>
                    <p style={{ marginBottom: '0.5rem', color: 'var(--color-text)' }}>
                      Your phone number hasn't been verified yet, so SMS reminders can't be turned on.
                    </p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-light)', marginTop: '0.5rem', marginBottom: 0 }}>
                      If you missed the verification code that was sent, remove and re-enter your phone number on the{' '}
                      <button
                        onClick={() => changeTab('profile')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--color-primary)',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit'
                        }}
                      >
                        Profile tab
                      </button>{' '}
                      to get a new one.
                    </p>
                  </div>
                ) : (
                  <div>
                    {/* Enable SMS Reminders */}
                    <div className="settings-row">
                      <div className="settings-row-info">
                        <div className="settings-row-label">Enable SMS Reflection Reminders</div>
                        <p className="settings-row-description">Receive text message reminders to create reflections (in addition to email reminders)</p>
                      </div>
                      <div className="settings-row-control">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={smsRemindersEnabled}
                            onChange={async (e) => {
                              const newValue = e.target.checked;
                              setSmsRemindersEnabled(newValue);
                              try {
                                await api.put('/preferences', {
                                  preferenceKey: 'sms_reminders_enabled',
                                  preferenceValue: newValue
                                });
                              } catch (error) {
                                console.error('Error saving SMS reminder preference:', error);
                                setSmsRemindersEnabled(!newValue);
                              }
                            }}
                          />
                          <span className={`toggle-switch-track${smsRemindersEnabled ? ' active' : ''}`}>
                            <span className={`toggle-switch-knob${smsRemindersEnabled ? ' active' : ''}`}></span>
                          </span>
                        </label>
                      </div>
                    </div>

                    {smsRemindersEnabled && (
                      <div className="settings-row">
                        <div className="settings-row-info">
                          <div className="settings-row-label">Reminder Frequency</div>
                          <p className="settings-row-description">Choose how often you want to receive SMS reminders</p>
                        </div>
                        <div className="settings-row-control">
                          <select
                            value={smsReminderFrequency}
                            onChange={async (e) => {
                              const newFreq = e.target.value;
                              setSmsReminderFrequency(newFreq);
                              try {
                                await api.put('/preferences', {
                                  preferenceKey: 'sms_reminder_frequency',
                                  preferenceValue: newFreq
                                });
                              } catch (error) {
                                console.error('Error saving SMS reminder frequency:', error);
                              }
                            }}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Bi-weekly (Every 2 weeks)</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                      </div>
                    )}

                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      backgroundColor: 'var(--color-background-secondary)',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      color: 'var(--color-text-light)'
                    }}>
                      <strong>Note:</strong> Standard messaging rates may apply. You can opt out at any time by
                      replying STOP to any reminder message or by disabling this setting.
                    </div>
                  </div>
                )}
              </div>

              {/* Friend Prompt Suggestions */}
              <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--color-border)', paddingTop: '1.5rem' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', color: 'var(--color-text)' }}>
                  💌 Friend Prompt Suggestions
                </h4>

                <div className="settings-row">
                  <div className="settings-row-info">
                    <div className="settings-row-label">Notify me when friends suggest prompts</div>
                    <p className="settings-row-description">
                      Receive a notification when a friend creates a prompt suggestion just for you.
                    </p>
                  </div>
                  <div className="settings-row-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={preferences.notify_friend_suggestions !== false}
                        onChange={(e) => handlePreferenceChange('notify_friend_suggestions', e.target.checked)}
                        disabled={preferencesSaving}
                      />
                      <span className={`toggle-switch-track${preferences.notify_friend_suggestions !== false ? ' active' : ''}${preferencesSaving ? ' disabled' : ''}`}>
                        <span className={`toggle-switch-knob${preferences.notify_friend_suggestions !== false ? ' active' : ''}`}></span>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="settings-row">
                  <div className="settings-row-info">
                    <div className="settings-row-label">Notify me when a friend responds to my suggestion</div>
                    <p className="settings-row-description">
                      Get notified when someone records a reflection on a prompt you suggested to them.
                    </p>
                  </div>
                  <div className="settings-row-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={preferences.notify_when_friend_responds !== false}
                        onChange={(e) => handlePreferenceChange('notify_when_friend_responds', e.target.checked)}
                        disabled={preferencesSaving}
                      />
                      <span className={`toggle-switch-track${preferences.notify_when_friend_responds !== false ? ' active' : ''}${preferencesSaving ? ' disabled' : ''}`}>
                        <span className={`toggle-switch-knob${preferences.notify_when_friend_responds !== false ? ' active' : ''}`}></span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sharing' && (
            <div className="settings-section">
              <h3>Share Your Reflections</h3>
              <p className="section-description">Invite people to view your reflections</p>

              <form onSubmit={handleSendInvite} style={{ marginBottom: '2rem' }}>
                <div className="form-group">
                  <label>Invite Someone to View Your Reflections</label>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      placeholder="Enter email address"
                      style={{ flex: 1 }}
                      required
                    />
                    <button
                      type="submit"
                      disabled={sendingInvite}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: 'var(--color-primary)',
                        color: 'var(--color-button-text)',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '600',
                        cursor: sendingInvite ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {sendingInvite ? 'Sending...' : 'Send Invitation'}
                    </button>
                  </div>
                  <p className="field-description">
                    Enter the email address of the person you want to share with. They'll receive an invitation to view your reflections.
                  </p>
                </div>
              </form>

              {/* People I'm sharing with */}
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>People Viewing My Reflections</h4>
                {shareRelationships.sharingWith.length === 0 ? (
                  <p style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                    You haven't shared your reflections with anyone yet.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {shareRelationships.sharingWith.map((relationship) => (
                      <div
                        key={relationship.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem',
                          background: 'var(--color-background)',
                          borderRadius: '0.5rem',
                          border: '1px solid var(--color-border)'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                            {relationship.username}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                            {relationship.email}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                            Status: {relationship.status === 'active' ? '✓ Active' : `⏱ ${relationship.status}`}
                          </div>
                        </div>
                        {relationship.status === 'active' && (
                          <button
                            onClick={() => handleRevokeShare(relationship.id, relationship.username)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'var(--color-danger)',
                              color: 'var(--color-button-text)',
                              border: 'none',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            Revoke Access
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pending invitations I've sent */}
              {shareRelationships.pendingInvitations.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Pending Invitations</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {shareRelationships.pendingInvitations.filter(inv => inv.status === 'pending').map((invitation) => (
                      <div
                        key={invitation.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem',
                          background: 'var(--color-background)',
                          borderRadius: '0.5rem',
                          border: '1px solid var(--color-border)'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                            {invitation.invitee_email}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                            Sent: {formatDateOnly(invitation.created_at, user?.timezone)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleResendInvitation(invitation.invitee_email)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'var(--color-primary)',
                              color: 'var(--color-button-text)',
                              border: 'none',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            Resend Invite
                          </button>
                          <button
                            onClick={() => handleCancelInvitation(invitation.id, invitation.invitee_email)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'var(--color-text-secondary)',
                              color: 'var(--color-button-text)',
                              border: 'none',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invitations I've received */}
              {shareRelationships.sharedWithMe.filter(r => r.status === 'pending').length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Invitations You've Received</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {shareRelationships.sharedWithMe.filter(r => r.status === 'pending').map((relationship) => (
                      <div
                        key={relationship.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem',
                          background: 'var(--color-yellow-gradient-start)',
                          borderRadius: '0.5rem',
                          border: '2px solid var(--color-primary)'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                            {relationship.username} wants to share their reflections with you
                          </div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                            {relationship.email}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleAcceptShare(relationship.id, relationship.email)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'var(--color-success)',
                              color: 'var(--color-button-text)',
                              border: 'none',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleDeclineShare(relationship.id, relationship.email)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'var(--color-text-secondary)',
                              color: 'var(--color-button-text)',
                              border: 'none',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* People sharing with me (active) */}
              {shareRelationships.sharedWithMe.filter(r => r.status === 'active').length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>People Sharing With You</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {shareRelationships.sharedWithMe.filter(r => r.status === 'active').map((relationship) => (
                      <div
                        key={relationship.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem',
                          background: 'var(--color-background)',
                          borderRadius: '0.5rem',
                          border: '1px solid var(--color-border)'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                            {relationship.username}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                            {relationship.email}
                          </div>
                        </div>
                        <button
                          onClick={() => setRemoveShareModal({ show: true, relationship })}
                          className="btn-remove-share"
                        >
                          Remove Share
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Friends — people you can suggest prompts to */}
              <div style={{ marginTop: '2rem' }}>
                <h4 style={{ marginBottom: '0.5rem', fontSize: '1.1rem', color: 'var(--color-text)' }}>
                  💌 Friends
                </h4>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
                  Anyone you share reflections with (in either direction) can receive prompt suggestions from you.
                  Your suggestions appear in their <em>From Friends</em> category.
                </p>
                {friends.length === 0 ? (
                  <p style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic', fontSize: '0.875rem' }}>
                    No active friends yet. Accept a share invitation or have one accepted to see friends here.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {friends.map(friend => (
                      <div key={friend.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '1rem',
                        background: 'var(--color-background-secondary)',
                        borderRadius: '0.5rem',
                        border: '1px solid var(--color-border)'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>{friend.username}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{friend.email}</div>
                        </div>
                        <button
                          onClick={() => setFriendSuggestionModal({ show: true, friend })}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'var(--color-primary)',
                            color: 'var(--color-button-text, white)',
                            border: 'none',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          Suggest a Prompt
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Suggestion history — prompts I've sent to friends */}
              {mySuggestions.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: 'var(--color-text)' }}>
                    Prompts I've Suggested
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {mySuggestions.map(s => (
                      <div key={s.id} style={{
                        padding: '1rem',
                        background: 'var(--color-background-secondary)',
                        borderRadius: '0.5rem',
                        border: '1px solid var(--color-border)'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: 'var(--color-text)' }}>
                          {s.prompt_text}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span>To: <strong>{s.suggested_to_username}</strong></span>
                          <span>·</span>
                          <span>{formatDateOnly(s.created_at, user?.timezone)}</span>
                          <span>·</span>
                          {s.has_responded
                            ? <span style={{ color: 'var(--color-success, #16a34a)', fontWeight: '600' }}>✓ Responded</span>
                            : <span style={{ color: 'var(--color-text-secondary)' }}>Pending</span>}
                        </div>
                        {s.personal_note && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
                            Note: "{s.personal_note}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="settings-section">
              <h3>Plan &amp; Billing</h3>
              <p className="section-description">Manage your subscription and payment details</p>
              <CurrentPlanStatus
                subscription={subscription}
                storageStatus={subscriptionStorageStatus}
                onSubscriptionChange={(data) => setSubscription(data)}
              />
              <PlanComparisonCard
                currentSubscription={subscription}
                onSubscriptionChange={(data) => setSubscription(data)}
              />
            </div>
          )}

          {activeTab === 'family' && (
            <div className="settings-section">
              <h3>Family Group</h3>
              <p className="section-description">Manage your family members and shared archive</p>
              <FamilySettings subscription={subscription} />
            </div>
          )}
        </div>
      </div>

      {/* Friend Suggestion Modal */}
      {friendSuggestionModal.show && (
        <FriendSuggestionModal
          friend={friendSuggestionModal.friend}
          onClose={() => setFriendSuggestionModal({ show: false, friend: null })}
          onSuccess={fetchMySuggestions}
        />
      )}

      {/* Remove Share Confirmation Modal */}
      {removeShareModal.show && (
        <div className="modal">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <h3 style={{ marginBottom: '1.5rem' }}>Remove Share</h3>
            <p style={{ marginBottom: '1.5rem', lineHeight: '1.6' }}>
              Removing the share will no longer let you see the reflections from{' '}
              <strong>{removeShareModal.relationship?.username}</strong>. They will need to
              re-invite you to see them in the future.
            </p>
            <div className="modal-buttons">
              <button
                onClick={() => setRemoveShareModal({ show: false, relationship: null })}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveShare}
                className="btn-continue"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div className="modal-overlay" onClick={() => {
          setShowDeleteAccountModal(false);
          setDeletePassword('');
          setDeletePasswordError('');
        }}>
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1.5rem', color: 'var(--color-danger)' }}>Delete Account</h3>
            <p style={{ marginBottom: '1.5rem', lineHeight: '1.6', fontWeight: 'bold', color: 'var(--color-danger)' }}>
              ⚠️ WARNING: This action is permanent and cannot be undone!
            </p>
            <p style={{ marginBottom: '1.5rem', lineHeight: '1.6' }}>
              Deleting your account will immediately and permanently delete:
            </p>
            <ul style={{ marginBottom: '1.5rem', marginLeft: '2rem', lineHeight: '1.8' }}>
              <li>All of your reflections (video, audio, and text)</li>
              <li>Your user profile and settings</li>
              <li>All shares to and from other users</li>
              <li>All data associated with your account</li>
            </ul>
            <p style={{ marginBottom: '1.5rem', lineHeight: '1.6', fontWeight: 'bold' }}>
              This is your last chance to cancel.
            </p>

            <form onSubmit={handleDeleteAccount}>
              <div className="form-group">
                <label htmlFor="deletePassword">Enter your password to confirm:</label>
                <PasswordInput
                  name="deletePassword"
                  value={deletePassword}
                  onChange={(e) => {
                    setDeletePassword(e.target.value);
                    setDeletePasswordError('');
                  }}
                  required
                  placeholder="Enter your password"
                  className={deletePasswordError ? 'password-error' : ''}
                />
                {deletePasswordError && (
                  <p style={{ color: 'var(--color-danger)', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                    {deletePasswordError}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteAccountModal(false);
                    setDeletePassword('');
                    setDeletePasswordError('');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'var(--color-border)',
                    color: 'var(--color-text)',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'var(--color-danger)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Delete Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Phone Verification Modal */}
      {showPhoneVerification && (
        <div className="modal-overlay" onClick={() => setShowPhoneVerification(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>Verify Phone Number</h2>
            <p style={{ marginBottom: '1.5rem', color: 'var(--color-text)' }}>
              A verification code has been sent to <strong>{pendingPhone}</strong>. Please enter the 6-digit code below.
            </p>
            <form onSubmit={handleVerifyPhoneCode}>
              <div className="form-group">
                <label>Verification Code</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  placeholder="Enter 6-digit code"
                  maxLength="6"
                  pattern="\d{6}"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    border: '1px solid var(--color-border)',
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--color-bg)',
                    color: 'var(--color-text)'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  Verify
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPhoneVerification(false);
                    setVerificationCode('');
                    setPendingPhone('');
                  }}
                  className="btn-cancel"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deletion Complete Modal */}
      {showDeletionComplete && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '400px' }}>
            <h3 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>Account Deleted</h3>
            <p style={{ marginBottom: '2rem', lineHeight: '1.6', textAlign: 'center' }}>
              Your account and all associated data have been permanently deleted.
            </p>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleDeletionComplete}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '1rem'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
